import React, { Suspense, lazy, useCallback, useEffect, useId, useState } from "react";
import { BadgeCheck, ClipboardCheck, Eye, Footprints, PencilLine, WifiOff } from "lucide-react";
import { Button } from "../../../../components/shadcn/ui/button";
import "./encounter-workbench.css";
import {
  STATUS_WORDS,
  UNREACHED_WORDS,
  clinicalApi,
  dayWords,
  isEncounter,
  refusalWords,
  startBlockedWords,
  unanswered,
  useActionKeys,
  useViewingAs,
  viewingWords,
  whenWords,
} from "./clinicalApi";
import { podiatryOpenIn, usePractices } from "./PracticeSwitcher";
import type { EncounterStatus, EncounterSummary, PatientEncounters } from "./types";

/**
 * The resident's podiatry encounters, inside the resident workbench next to the private notes (a care-assigned row).
 *
 * It renders NOTHING for a provider who has no Podiatry here: the server's answers decide (the encounters list,
 * `can_start`, and the practices status), so an Audiology-only provider sees no change. It is a <section>, never a
 * <form>: it sits inside the manifest form's portal tree and forms do not nest.
 *
 * In a read-only "Sign in as" session (ruling 2026-10-29) the same server answers decide what shows, evaluated for the
 * person viewed. Nothing is started: "Start podiatry encounter" (a POST, refused by the server) is replaced by "View the
 * Podiatry form", which opens the workbench on the current template with every field disabled and no draft created.
 */
export interface ClinicalEncountersPanelProps {
  facilityId: string;
  patientId: string;
  patientName: string;
  /** For the workbench header only. */
  facilityName?: string | null;
}

type Load =
  | { state: "loading" }
  | { state: "ready"; page: PatientEncounters }
  | { state: "failed"; message: string; refused: boolean };

const STATUS_ICON: Record<EncounterStatus, typeof PencilLine> = {
  DRAFT: PencilLine,
  READY_FOR_SIGNATURE: ClipboardCheck,
  SIGNED: BadgeCheck,
};

// Loaded only when an encounter is opened: a provider with no Podiatry never downloads the workbench or the form kit.
const EncounterWorkbench = lazy(() => import("./EncounterWorkbench"));

const START_UNCONFIRMED = `${UNREACHED_WORDS} the start. Nothing was created that you can see. Try again; it will not be started twice.`;

function isPage(v: unknown): v is PatientEncounters {
  return !!v && typeof v === "object" && Array.isArray((v as PatientEncounters).encounters) && typeof (v as PatientEncounters).can_start === "boolean";
}

export function ClinicalEncountersPanel({ facilityId, patientId, patientName, facilityName }: ClinicalEncountersPanelProps) {
  const headingId = useId();
  const { keyFor, settle } = useActionKeys();
  const practices = usePractices(facilityId);
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const viewing = useViewingAs();

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const r = await clinicalApi.patientEncounters(patientId, facilityId);
      if (r.success && isPage(r.data)) setLoad({ state: "ready", page: r.data });
      else if (r.success) setLoad({ state: "failed", message: "The podiatry encounters could not be read.", refused: true });
      else setLoad({ state: "failed", message: refusalWords(r, "load the podiatry encounters"), refused: !unanswered(r) && (r.status === 403 || r.status === 404) });
    } catch {
      setLoad({ state: "failed", message: "Could not reach eMerge to load the podiatry encounters.", refused: false });
    }
  }, [patientId, facilityId]);

  useEffect(() => {
    setLoad({ state: "loading" });
    setStartError("");
    setOpenId(null);
    setPreviewing(false);
    fetchList();
  }, [fetchList]);

  const start = async () => {
    // Never while viewing: the button is not offered, and the server would refuse the POST anyway.
    if (starting || viewing) return;
    setStarting(true);
    setStartError("");
    const action = `create:${facilityId}:${patientId}`;
    try {
      const r = await clinicalApi.create(patientId, facilityId, keyFor(action));
      if (r.success && r.data && isEncounter(r.data.encounter)) {
        settle(action);
        // `existing`: the caller already had one open for this resident; open that one instead of a second.
        setOpenId(r.data.encounter.id);
        fetchList();
      } else if (unanswered(r)) {
        // Key kept: trying again is the same start, created once.
        setStartError(START_UNCONFIRMED);
      } else {
        settle(action);
        setStartError(
          r.code ? r.message || startBlockedWords(r.code) : r.status === 404 ? "Podiatry documentation is not open right now. Nothing was created." : r.message || "The encounter was not started.",
        );
      }
    } catch {
      setStartError(START_UNCONFIRMED);
    } finally {
      setStarting(false);
    }
  };

  const page = load.state === "ready" ? load.page : null;
  const podiatryOpen = podiatryOpenIn(practices);

  // Who sees the panel: the server decides. Nothing for a provider with no Podiatry here.
  let visible = false;
  if (page) visible = page.encounters.length > 0 || page.can_start || (podiatryOpen && !!page.start_blocked_reason);
  else if (load.state === "failed") visible = !load.refused && podiatryOpen;
  if (!visible && !openId && !previewing) return null;

  const rows = page ? page.encounters : [];
  const canStart = !!page && page.can_start;

  const open = (e: EncounterSummary) => {
    const verb = e.status === "DRAFT" && e.is_mine && !viewing ? "Continue draft" : "Open";
    return (
      <Button type="button" variant="outline" onClick={() => setOpenId(e.id)} aria-label={`${verb} podiatry encounter of ${dayWords(e.encounter_date)}${viewing ? " (read-only)" : ""}`}>
        {verb}
      </Button>
    );
  };

  return (
    <section className="fw-cx-panel" aria-labelledby={headingId} data-testid="clinical-encounters-panel">
      <h3 id={headingId} className="fw-replacement-title">
        <Footprints size={14} aria-hidden="true" /> Podiatry encounters
      </h3>

      {viewing && (
        <p className="fw-cx-viewing" role="note" data-testid="viewing-note">
          <Eye size={12} aria-hidden="true" /> {viewingWords(viewing)}
        </p>
      )}

      {load.state === "failed" && visible && (
        <div role="alert" className="fw-cx-error-box">
          <p>{load.message}</p>
          <Button type="button" variant="outline" onClick={() => { setLoad({ state: "loading" }); fetchList(); }}>
            Try again
          </Button>
        </div>
      )}

      {page && (
        <>
          {rows.length === 0 ? (
            <p className="fw-muted">No podiatry encounters for this resident yet.</p>
          ) : (
            <ul className="fw-cx-list" aria-label="Podiatry encounters, newest first">
              {rows.map((e) => {
                const Icon = STATUS_ICON[e.status] || PencilLine;
                return (
                  <li key={e.id} className="fw-cx-row">
                    <div className="fw-cx-row-main">
                      <strong>{dayWords(e.encounter_date)}</strong>
                      <span className={`fw-cx-status ${e.status === "SIGNED" ? "signed" : e.status === "READY_FOR_SIGNATURE" ? "ready" : "draft"}`}>
                        <Icon aria-hidden="true" /> {STATUS_WORDS[e.status] || e.status}
                      </span>
                    </div>
                    <p className="fw-muted">
                      {e.author_name || "Author not recorded"}
                      {e.is_mine ? " · yours" : ""}
                      {e.signed_at ? ` · signed ${whenWords(e.signed_at)}` : ""}
                      {e.addenda_count > 0 ? ` · ${e.addenda_count} ${e.addenda_count === 1 ? "addendum" : "addenda"}` : ""}
                    </p>
                    {open(e)}
                  </li>
                );
              })}
            </ul>
          )}

          {viewing ? (
            <div className="fw-cx-start">
              <Button type="button" variant="outline" onClick={() => setPreviewing(true)} data-testid="view-form">
                <Eye aria-hidden="true" /> View the Podiatry form
              </Button>
              {!canStart && (
                <p className="fw-muted" data-testid="start-blocked">
                  {startBlockedWords(page.start_blocked_reason)}
                </p>
              )}
            </div>
          ) : canStart ? (
            <div className="fw-cx-start">
              <Button type="button" className="fw-primary" disabled={starting || !online} onClick={start}>
                {starting ? "Starting…" : "Start podiatry encounter"}
              </Button>
              {!online && (
                <p className="fw-muted">
                  <WifiOff size={12} aria-hidden="true" /> Offline: an encounter cannot be started until eMerge is reachable.
                </p>
              )}
              {startError && (
                <p role="alert" className="fw-cx-error">
                  {startError}
                </p>
              )}
            </div>
          ) : (
            <p className="fw-muted" data-testid="start-blocked">
              {startBlockedWords(page.start_blocked_reason)}
            </p>
          )}
        </>
      )}

      {previewing && !openId && (
        <Suspense fallback={<p role="status" className="fw-muted">Opening the form…</p>}>
          <EncounterWorkbench encounterId={null} patientName={patientName} facilityName={facilityName} onClose={() => setPreviewing(false)} />
        </Suspense>
      )}

      {openId && (
        <Suspense fallback={<p role="status" className="fw-muted">Opening the encounter…</p>}>
          <EncounterWorkbench
            encounterId={openId}
            patientName={patientName}
            facilityName={facilityName}
            onClose={() => {
              setOpenId(null);
              fetchList();
            }}
            onChanged={fetchList}
          />
        </Suspense>
      )}
    </section>
  );
}

export default ClinicalEncountersPanel;
